import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { catchError, tap } from 'rxjs/operators';

import { ApiUserService } from '../services';
import { SnackbarService } from '@fitscovery/ui/snackbar';

import { UserActions } from '../actions';
import { User, UserResponse, FitnessConsumerProfile, FitnessConsumerProfileResponse } from '../models';
import { PartnerRoleResponse, VendorAdminResponse } from '@fitscovery/partner/data-access';
import { UserStateModel } from '../types';
import { UserControlState } from './user-control-state';

export const userStateDefaults: UserStateModel = {
  info: null,
  fitnessConsumerProfile: null,
  partners: null,
  loaded: false
};

export const USER_STATE_TOKEN = new StateToken<UserStateModel>('user');

@State<UserStateModel>({
  name: USER_STATE_TOKEN,
  defaults: userStateDefaults
})

@Injectable()
export class UserState extends UserControlState implements NgxsOnInit {

  @Selector()
  static user(state: UserStateModel): User | null {
    return state.info;
  }

  @Selector()
  static partners(state: UserStateModel): PartnerRoleResponse[] | null {
    return state.partners;
  }

  @Selector()
  static hasVendorPartners(state: UserStateModel): boolean {
    return state.partners!?.length > 0;
  }

  @Selector()
  static fitnessConsumerProfile(state: UserStateModel): FitnessConsumerProfile | null {
    return state.fitnessConsumerProfile;
  }

  constructor(
    private userService: ApiUserService,
    public override snackbar: SnackbarService
  ) {
    super(snackbar);
  }

  ngxsOnInit(): void {
  }

  @Action(UserActions.GetCurrentUser)
  getCurrentUser(ctx: StateContext<UserStateModel>) {
    return this.userService.getCurrentUser().pipe(
      tap((response: UserResponse) => ctx.patchState({ info: response.data[0], loaded: true })),
      this.catchResponse
    );
  }

  @Action(UserActions.GetUserPartners)
  getUserPartners(ctx: StateContext<UserStateModel>) {
    const userId = ctx.getState().info!.id;
    return this.userService.getUserPartners(userId!).pipe(
      tap((response: VendorAdminResponse) => ctx.patchState({ partners: response.data.partners, loaded: true })),
      catchError(e => { throw new Error(e) })
    );
  }

  @Action(UserActions.GetFitnessConsumerProfile)
  getFitnessConsumerProfileByUserId(ctx: StateContext<UserStateModel>) {
    return this.userService.getFitnessConsumerProfileByUserId(ctx.getState().info!.id!).pipe(
      tap((response: FitnessConsumerProfileResponse) => ctx.patchState({ fitnessConsumerProfile: response.data })),
      this.catchResponse
    );
  }

  @Action(UserActions.UpdateFitnessConsumerProfile)
  @ImmutableContext()
  updateFitnessConsumerProfile(ctx: StateContext<UserStateModel>, action: UserActions.UpdateFitnessConsumerProfile) {
    return this.userService.updateFitnessConsumerProfile(action.profile).pipe(
      tap(() => ctx.setState((state: UserStateModel) => {
        state.fitnessConsumerProfile!.username = action.profile.username
        state.fitnessConsumerProfile!.isProfilePrivate = action.profile.isProfilePrivate
        return state;
      })),
      this.catchResponse
    );
  }

}
